import {useEffect, useRef, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useIsFetching} from 'react-query';
import PageLoader from '../common/components/PageLoader';
import usePageTitle from './usePageTitle';

const PageTransitionLoader = (): JSX.Element => {
  const location = useLocation();
  usePageTitle();
  const isFetching = useIsFetching();
  const [isLoading, setIsLoading] = useState(true);
  const fetchingStarted = useRef(false);
  const prevPath = useRef<string | null>(null);

  // Réinitialise le loader à chaque changement de route
  useEffect(() => {
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;
    setIsLoading(true);
    fetchingStarted.current = false;
  }, [location.pathname]);

  // Cache le loader quand toutes les requêtes React Query sont terminées
  useEffect(() => {
    if (!isLoading) return;
    if (isFetching > 0) {
      fetchingStarted.current = true;
    } else if (fetchingStarted.current) {
      setIsLoading(false);
    }
  }, [isFetching, isLoading]);

  // Pages sans requêtes : cache le loader après 500ms si rien n'a démarré
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => {
      if (!fetchingStarted.current) setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Sécurité absolue : cache le loader après 8s dans tous les cas
  useEffect(() => {
    if (!isLoading) return;
    const fallback = setTimeout(() => setIsLoading(false), 8000);
    return () => clearTimeout(fallback);
  }, [isLoading]);

  const [isVisible, setIsVisible] = useState(isLoading);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setIsFadingOut(false);
      setIsVisible(true);
    } else if (isVisible) {
      setIsFadingOut(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsFadingOut(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isVisible]);

  return (
    <div style={{position: 'relative', minHeight: '60vh'}}>
      {isVisible && <PageLoader fadingOut={isFadingOut} />}
      <div style={{visibility: isVisible ? 'hidden' : 'visible'}}>
        <Outlet />
      </div>
    </div>
  );
};

export default PageTransitionLoader;
